import * as d3 from 'd3';
import _ from 'lodash';

import Constants from '../Constants';
import ImageRegistry from '../ImageRegistry';
import RenderingState from '../RenderingState';
import { getTopicUrl, getTopicWord } from '../topicUtils';
import TopicVizScaling from '../TopicVizScaling';
import LabeledImage from '../VirtualDom/drawElements/LabeledImage';
import VirtualDom from '../VirtualDom/VirtualDom';
import VirtualDomLayer from '../VirtualDom/VirtualDomLayer';

export default function createSubTopicLevel(
  clusterInfo,
  virtualDom: VirtualDom,
  scaling: TopicVizScaling,
  imageRegistry: ImageRegistry,
  renderingState: RenderingState,
) {
  const scaleFactor = scaling.getScaleFactor();

  const scaleNumberOfSubTopicsByClusterSize = d3
    .scaleLinear()
    .domain([10, 50])
    .range([Constants.subTopics.minCount, Constants.subTopics.maxCount]);

  const documentPointsByClusterId = {};

  for (const documentPoint of virtualDom
    .getLayer('documentPoints')
    .getElements()) {
    const clusterId = documentPoint.getAttribute('clusterId');
    if (!(clusterId in documentPointsByClusterId)) {
      documentPointsByClusterId[clusterId] = [documentPoint];
    } else {
      documentPointsByClusterId[clusterId].push(documentPoint);
    }
  }

  _.sortBy(
    clusterInfo.filter(
      cluster => cluster['cluster_index'] in documentPointsByClusterId,
    ),
    cluster => -documentPointsByClusterId[cluster['cluster_index']].length,
  );
  const subTopicElements = [];
  for (const topic of virtualDom.getLayer('topicElements').getElements()) {
    const documentsInTopic = topic.get('countDocuments');
    const clusterIndex = topic.get('clusterIndex');

    const cluster = clusterInfo[clusterIndex];
    const numSubTopics = Math.min(
      8,
      scaleNumberOfSubTopicsByClusterSize(documentsInTopic),
      cluster['words'].length,
    );
    const _subTopics = [];
    for (let i = 0; i < numSubTopics; i++) {
      const y_row = Math.floor(i / 2);
      const x_row = i % 2;

      _subTopics.push({
        x: topic.get('x') - x_row * scaleFactor * 120,
        y: topic.get('y') + y_row * scaleFactor * 25,
        clusterIndex: clusterIndex,
        word: getTopicWord(cluster, i).toUpperCase(),
        color: scaling.colorScale(clusterIndex),
        url: getTopicUrl(cluster, i),
      });
    }

    for (const subTopic of _subTopics) {
      const subTopicElement = new LabeledImage({
        x: subTopic['x'],
        y: subTopic['y'],
        label: subTopic['word'].toUpperCase(),
        fontSize: scaling.getScaleFactor() * Constants.subTopics.fontSize,
        url: null,
        maxCharacterWidth: Constants.subTopics.maxStringLength,
        imageRegistry: imageRegistry,
        maxLines: Constants.subTopics.maxLines,
        borderColor: subTopic['color'],
        onRequestDraw: () => renderingState.setDirtyBit(true),
      });
      subTopicElement.set('clusterIndex', subTopic['clusterIndex']);
      imageRegistry.register(subTopic['url'], subTopicElement);
      subTopicElements.push(subTopicElement);
    }
  }

  const subTopicSelection = new VirtualDomLayer({
    name: 'subtopicElements',
    elements: subTopicElements,
    keyFramesOpacity: [
      [Constants.thresholds.subTopic[0] - 0.2, 0],
      [Constants.thresholds.subTopic[0], 1],
      [Constants.thresholds.subTopic[1], 1],
      [Constants.thresholds.subTopic[1] + 0.2, 0],
    ],
    showAtInitialZoom: false,
  });

  virtualDom.registerLayer(subTopicSelection);
}
